import { GridFilterInputValueProps, GridFilterOperator } from '@mui/x-data-grid-pro'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs, { Dayjs } from 'dayjs'
import { Stack } from '@mui/material'
import {
  inventoryV3DateOperatorMatches,
  inventoryV3DateRangeValue,
  isInventoryV3DateFilterReady,
  parseInventoryV3CalendarDay,
} from './inventoryV3DateFilter'

const toDayjs = (value: unknown): Dayjs | null => {
  const day = parseInventoryV3CalendarDay(value)
  return day === null ? null : dayjs(day)
}

const toYmd = (value: Dayjs | null): string => (value && value.isValid() ? value.format('YYYY-MM-DD') : '')

const pickerTextField = (variant: 'outlined' | 'standard', placeholder: string) => ({
  size: 'small' as const,
  fullWidth: true,
  variant,
  placeholder,
})

export const InventoryV3DatePicker = ({
  value,
  onChange,
  label,
  variant = 'outlined',
}: {
  value: unknown
  onChange: (ymd: string) => void
  label?: string
  variant?: 'outlined' | 'standard'
}) => (
  <LocalizationProvider dateAdapter={AdapterDayjs}>
    <DatePicker
      value={toDayjs(value)}
      onChange={next => onChange(toYmd(next))}
      slotProps={{
        textField: pickerTextField(variant, label ?? 'Date'),
        openPickerButton: { size: 'small' },
      }}
    />
  </LocalizationProvider>
)

export const InventoryV3DateRangePicker = ({
  value,
  onChange,
  variant = 'outlined',
}: {
  value: unknown
  onChange: (range: [string, string]) => void
  variant?: 'outlined' | 'standard'
}) => {
  const [start, end] = inventoryV3DateRangeValue(value)
  return (
    <Stack direction={variant === 'standard' ? 'row' : 'column'} spacing={1} sx={{ width: '100%', minWidth: 0 }}>
      <InventoryV3DatePicker
        variant={variant}
        label='Start'
        value={start}
        onChange={next => onChange([next, end])}
      />
      <InventoryV3DatePicker variant={variant} label='End' value={end} onChange={next => onChange([start, next])} />
    </Stack>
  )
}

const InventoryV3DateFilterInput = ({ item, applyValue }: GridFilterInputValueProps) => (
  <InventoryV3DatePicker
    variant='standard'
    value={item.value}
    onChange={ymd => applyValue({ ...item, value: ymd || undefined })}
  />
)

const InventoryV3DateRangeFilterInput = ({ item, applyValue }: GridFilterInputValueProps) => (
  <Stack sx={{ minWidth: 0, width: '100%' }}>
    <InventoryV3DateRangePicker
      variant='standard'
      value={item.value}
      onChange={range => applyValue({ ...item, value: range[0] || range[1] ? range : undefined })}
    />
  </Stack>
)

const applyDateFilter =
  (operator: string): GridFilterOperator['getApplyFilterFn'] =>
  filterItem => {
    if (operator === 'isEmpty') return value => value == null || value === ''
    if (operator === 'isNotEmpty') return value => value != null && value !== ''
    if (!isInventoryV3DateFilterReady(filterItem)) return null
    return value => inventoryV3DateOperatorMatches(value, operator, filterItem.value) === true
  }

const singleDateOperator = (value: string, label: string): GridFilterOperator => ({
  value,
  label,
  headerLabel: label,
  getApplyFilterFn: applyDateFilter(value),
  InputComponent: InventoryV3DateFilterInput,
})

export const INVENTORY_V3_DATE_FILTER_OPERATORS: GridFilterOperator[] = [
  singleDateOperator('is', 'On'),
  singleDateOperator('before', 'Before'),
  singleDateOperator('after', 'After'),
  {
    value: 'between',
    label: 'Between',
    headerLabel: 'Between',
    getApplyFilterFn: applyDateFilter('between'),
    InputComponent: InventoryV3DateRangeFilterInput,
  },
  {
    value: 'isEmpty',
    label: 'is empty',
    headerLabel: 'is empty',
    getApplyFilterFn: applyDateFilter('isEmpty'),
    requiresFilterValue: false,
  },
  {
    value: 'isNotEmpty',
    label: 'is not empty',
    headerLabel: 'is not empty',
    getApplyFilterFn: applyDateFilter('isNotEmpty'),
    requiresFilterValue: false,
  },
]
